/**
 * `TagChip` — the stamped ring. `docs/design-direction.md` §9.7.
 *
 * A 999px ring in 1px `--accent-quiet` over nothing, mono caps `--fs-11`, **44px
 * tall** — the target is the object, so it needs no hit expander. Active fills solid
 * `--accent-quiet` with knocked-out `--ink-000` type. Two radii exist in this system
 * and this is the round one: circles are records and touch targets (§5.3).
 *
 * Where it appears: the six stamped ring chips under the search zero state
 * (`DANCEHALL · ROOTS · STEPPERS · 1988 · SLENG TENG · DUBPLATE`, §8.3) and the
 * `A–Z / RECENT / RANDOM` switch on My Crate (§8.5).
 *
 * A strip of these scrolls with momentum, hides its scrollbar and fades 16px at both
 * ends with a `mask-image`, so the row reads as continuing rather than ending (§9.7)
 * — that mask belongs to the strip, not to the chip, and it is licensed because a
 * ring is a mark, not 13px copy (§12.11).
 *
 * As with `Chip`: `aria-pressed` only when it is really a toggle, no haptics inside
 * a primitive, and Cyrillic copy keeps the tier but drops the tracking.
 */

import type { JSX } from 'react';

import { hasCyrillic } from '@bl/shared';

import styles from './primitives.module.css';
import { cx } from './Rule';

export interface TagChipProps {
  /** The printed label. */
  readonly label: string;
  /** Active rings fill solid `--accent-quiet` with knocked-out type. */
  readonly active?: boolean;
  readonly disabled?: boolean;
  /** Omit for a static tag: without it the ring is not a control. */
  readonly onToggle?: (() => void) | undefined;
  readonly className?: string | undefined;
}

export function TagChip({
  label,
  active = false,
  disabled = false,
  onToggle,
  className,
}: TagChipProps): JSX.Element {
  const classes = cx(
    styles.tag,
    styles.hit,
    't-mono',
    't-11',
    hasCyrillic(label) && styles.cyr,
    className,
  );

  if (onToggle === undefined) {
    return (
      <span className={classes} data-active={active ? 'true' : 'false'}>
        {label}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      data-active={active ? 'true' : 'false'}
      aria-pressed={active}
      disabled={disabled}
      onClick={onToggle}
    >
      {label}
    </button>
  );
}
